import React, { useEffect, useRef, useState } from 'react'

/**
 * Scroll header horizontally on smaller devices
 */
const scrollHeader = (header: HTMLDivElement, id: string) => {
  if (window.innerWidth < 1024) {
    const el = header?.querySelector(`[data-id=${id}]`)?.parentElement
    if (el && el instanceof HTMLLIElement) {
      header?.scrollBy({
        left: el.offsetLeft - header?.scrollLeft - 16,
        behavior: 'smooth',
      })
    }
  }
}

/**
 * Intersection observer for sections
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 */

type Props = {
  root: React.RefObject<HTMLDivElement>
  header: React.RefObject<HTMLDivElement>
  selector?: string
}

/**
 * Options
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver#instance_properties
 */
const options = {
  root: null, // observe document
  rootMargin: '0px',
  threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
}

export const useSectionsObserver = ({
  root,
  header,
  selector = 'section',
}: Props) => {
  const observer = useRef<IntersectionObserver>()

  // active section id reference for use inside useEffect hook
  const active = useRef<string | null>(null)

  // active section id state
  const [activeId, setActiveId] = useState<string | null>(null)

  useEffect(() => {
    if (!root.current || !header.current) return
    // create observer
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const id = entry.target.id
        const ih = entry.intersectionRect.height
        const rh = entry.rootBounds?.height
        const moreThanHalf = ih && rh && ih > rh / 2
        // trigger change if more than half of section is in viewport
        if (entry.isIntersecting && moreThanHalf && active.current !== id) {
          active.current = id
          scrollHeader(header.current!, id)
          setActiveId(id)
        }
      })
    }, options)

    // observe all sections
    const elements = root.current.querySelectorAll(selector)
    for (let i = 0; i < elements.length; i++) {
      observer.current.observe(elements[i])
    }

    // remove observer on unmount
    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [])

  return activeId
}
